import { Theme } from '@monorepo/relayably/theme'
import React, { useMemo, useState } from 'react'
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native'
import { DashboardCallSummary } from '../types'

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : value
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const formatDuration = (seconds: number) => {
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `${minutes}m`
}

export const CallsScreen = ({
  theme,
  calls,
  direction: controlledDirection,
  onDirectionChange,
  onOpenCall,
}: {
  theme: Theme
  calls: DashboardCallSummary[]
  direction?: 'inbound' | 'outbound'
  onDirectionChange?: (direction: 'inbound' | 'outbound') => void
  onOpenCall: (call: DashboardCallSummary) => void
}) => {
  const { width } = useWindowDimensions()
  const isCompact = width < 760
  const [uncontrolledDirection, setUncontrolledDirection] = useState<'inbound' | 'outbound'>('inbound')
  const direction = controlledDirection ?? uncontrolledDirection
  const setDirection = (nextDirection: 'inbound' | 'outbound') => {
    if (controlledDirection === undefined) {
      setUncontrolledDirection(nextDirection)
    }
    onDirectionChange?.(nextDirection)
  }
  const [search, setSearch] = useState('')
  const styles = createStyles(theme)

  const filteredCalls = useMemo(
    () =>
      calls.filter(call => {
        if (call.direction !== direction) {
          return false
        }

        const haystack = `${call.phoneNumber} ${call.summary} ${call.agentName} ${call.categoryLabel}`.toLowerCase()
        return haystack.includes(search.trim().toLowerCase())
      }),
    [calls, direction, search]
  )

  return (
    <View style={styles.screen}>
      {isCompact ? (
        <View style={styles.topArea}>
          <View style={styles.toggleRow}>
            {(['inbound', 'outbound'] as const).map(item => (
              <Pressable
                key={item}
                onPress={() => setDirection(item)}
                style={[styles.toggleButton, direction === item && styles.toggleButtonActive]}
              >
                <Text style={[styles.toggleText, direction === item && styles.toggleTextActive]}>
                  {item === 'inbound' ? 'Inbound' : 'Outbound'}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      ) : null}
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <TextInput
          placeholder="Search a number, summary, or agent"
          placeholderTextColor={theme.colors.inactive}
          value={search}
          onChangeText={setSearch}
          style={styles.searchInput}
        />

        <View style={styles.list}>
          {filteredCalls.map(call => (
            <Pressable key={call.id} onPress={() => onOpenCall(call)} style={styles.card}>
              <View style={styles.cardHeader}>
                <View>
                  <Text style={styles.phone}>{call.phoneNumber}</Text>
                  <Text style={styles.meta}>
                    {new Date(call.startedAt).toLocaleString()} • {formatDuration(call.durationSeconds)}
                  </Text>
                </View>
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{call.categoryLabel}</Text>
                </View>
              </View>
              <Text style={styles.summary}>{call.summary || 'Open to read the full transcript and outcome.'}</Text>
              <Text style={styles.footer}>{call.agentName}</Text>
            </Pressable>
          ))}

          {!filteredCalls.length ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>No calls yet</Text>
              <Text style={styles.emptyBody}>Once Relayably starts handling calls, they’ll show up here with summaries and transcripts.</Text>
            </View>
          ) : null}
        </View>
      </ScrollView>
    </View>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: {
      flex: 1,
    },
    topArea: {
      paddingBottom: 12,
    },
    content: {
      gap: 16,
      paddingBottom: 20,
    },
    toggleRow: {
      flexDirection: 'row',
      gap: 0,
      alignSelf: 'flex-start',
      borderRadius: 18,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      backgroundColor: hexToRgba(theme.colors.surface, 0.94),
    },
    toggleButton: {
      minWidth: 132,
      paddingHorizontal: 20,
      paddingVertical: 13,
      backgroundColor: 'transparent',
      alignItems: 'center',
      borderRightWidth: 1,
      borderRightColor: hexToRgba(theme.colors.primary, 0.08),
    },
    toggleButtonActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
    },
    toggleText: {
      color: theme.colors.inactive,
      fontSize: 14,
      fontWeight: '700',
    },
    toggleTextActive: {
      color: theme.colors.text,
    },
    searchInput: {
      borderRadius: 18,
      paddingHorizontal: 16,
      paddingVertical: 14,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.1),
      color: theme.colors.text,
      fontSize: 14,
    },
    list: {
      gap: 12,
    },
    card: {
      borderRadius: 20,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 10,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
    },
    phone: {
      color: theme.colors.text,
      fontSize: 16,
      fontWeight: '700',
    },
    meta: {
      color: theme.colors.inactive,
      fontSize: 12,
      marginTop: 4,
    },
    badge: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 5,
      backgroundColor: hexToRgba(theme.colors.primary, 0.1),
    },
    badgeText: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
    },
    summary: {
      color: theme.colors.text,
      fontSize: 14,
      lineHeight: 22,
    },
    footer: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '600',
    },
    emptyCard: {
      borderRadius: 20,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.9),
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: hexToRgba(theme.colors.primary, 0.16),
      gap: 8,
    },
    emptyTitle: {
      color: theme.colors.text,
      fontSize: 16,
      fontWeight: '700',
    },
    emptyBody: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
  })
